import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  BOOST_TYPES,
  BOOST_PLATFORMS,
  BOOST_PLATFORM_MAP,
  BOOST_QTY,
  BOOST_STATUS,
  POINT_PACKS,
  CUSTOM_TOPUP,
  TOPUP_BONUS,
  MODULE_ADDON_PRICES,
  QUOTA_ADDONS,
  QUOTA_ADDON_MAP,
  QUOTE_FEATURES,
  SERVICE_PACKAGES,
  approxUnits,
  boostCost,
  boostServiceOf,
  pointsForAmount,
  proratePrice,
  quoteFeatureTitle,
  validBoostUrl,
} from "../../config/addons";
import { FEATURE_MODULES, FEATURE_MODULE_MAP } from "../../config/feature-modules";
import { planLimit, lowestPlanWith } from "../../config/plans";
import { boostService, aiCreditService, addonService, customRequestService, storageService, ORDER_TYPE } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, DataTable, Input, PageHead, Qty, StatCard, Tabs, Textarea, baht, formatDate, money, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";
import PaymentQrModal from "../../components/PaymentQrModal";
import { useAddToCart } from "../../context/CartContext";
import { addonItem, pointsItem, customItem } from "../../config/cart";
import {
  ATTACH_MAX_BYTES, CUSTOM_MIN_RATE, CUSTOM_STATUS, INVALID_QUOTE_MESSAGE, INVALID_QUOTE_MESSAGE_EN, canCancelRequest, quotePayable, validateRequestForm,
} from "../../config/custom-request";

const TAB_KEYS = ["like", "follow", "topup", "ai", "features", "quota", "services", "custom"];

const pts = (value) => `${money(value, 0)} pt`;

export default function Addons() {
  const { organization, orgId, subscription, profile, refreshOrganizations, toast, checkStorage } = useApp();
  const { tx, pick } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = TAB_KEYS.includes(params.get("tab")) ? params.get("tab") : "like";
  const [checkout, setCheckout] = useState(null);
  const addToCart = useAddToCart();
  const cartPoints = ({ pack, amount }) => addToCart(pointsItem({ packKey: pack?.key, amount }));
  const cartAddon = ({ kind, key, blocks = 1, renewId = "" }) => addToCart(addonItem({ addonKind: kind, key, blocks, renewId }));

  const by = profile?.fullname || profile?.email || "";

  const { data: balance, reload: reloadBalance } = useAsync(() => boostService.balance(orgId), [orgId], 0);
  const { data: orders, reload: reloadOrders } = useAsync(() => boostService.orders(orgId), [orgId], []);
  const { data: ledger, reload: reloadLedger } = useAsync(() => boostService.ledger(orgId), [orgId], []);
  const { data: purchases, reload: reloadPurchases } = useAsync(() => addonService.history(orgId), [orgId], []);
  const { data: aiBalance, reload: reloadAiBalance } = useAsync(() => aiCreditService.balance(orgId), [orgId], 0);
  const { data: aiLedger, reload: reloadAiLedger } = useAsync(() => aiCreditService.ledger(orgId), [orgId], []);

  const { data: requests, reload: reloadRequests } = useAsync(() => customRequestService.listCustom(orgId), [orgId], []);

  const reloadPoints = () => {
    reloadBalance();
    reloadOrders();
    reloadLedger();
    reloadAiBalance();
    reloadAiLedger();
  };

  const activeOrders = (orders || []).filter((row) => row.status === "pending" || row.status === "in_progress").length;
  const myAddons = Array.isArray(organization?.addons) ? organization.addons : [];
  const activeAddonCount = myAddons.filter((row) => new Date(row.expireAt) > new Date()).length;

  // ---------- เปิดโมดัล QR (ใช้ร่วมทุกการซื้อในหน้านี้) ----------
  const buyPoints = ({ pack, amount }) => {
    const price = pack ? pack.price : amount;
    const points = pack ? pack.points : pointsForAmount(amount);
    setCheckout({
      title: pack ? pick(pack) : tx(`เติม ${money(points, 0)} point`, `Top up ${money(points, 0)} points`),
      amount: price,
      orderType: ORDER_TYPE.boostTopup,
      reference: `boost:${pack?.key || `custom_${price}`}`,
      productCode: `boost_${pack?.key || "custom"}`,
      detail: { orgId, pack: pack?.key || "", points },
      onPaid: async (transaction) => {
        await boostService.topup({ orgId, packKey: pack?.key, amount: price, transaction, by });
        reloadPoints();
        if (pack?.wallet === "ai") toast(tx(`ได้รับเครดิต AI ${money(points, 0)} แล้ว`, `${money(points, 0)} AI credits added`), "success");
        else toast(tx(`ได้รับ ${money(points, 0)} point แล้ว`, `${money(points, 0)} points added`), "success");
      },
    });
  };

  const buyAddon = ({ kind, key, blocks = 1, renewId = "", start }) => {
    const monthly = addonService.monthlyPrice({ kind, key, blocks });
    const { days, amount } = proratePrice(monthly, subscription.expireAt, start);
    const quota = QUOTA_ADDON_MAP[key];
    const name =
      kind === "module"
        ? `ฟีเจอร์เสริม ${FEATURE_MODULE_MAP[key]?.name || key}`
        : `${quota.name} +${money(quota.block * blocks, 0)} ${quota.unit}`;

    setCheckout({
      title: `${renewId ? "ต่ออายุ " : ""}${name} (${days} วัน)`,
      amount,
      orderType: ORDER_TYPE.addon,
      reference: `addon:${kind}:${key}:${renewId || "new"}`,
      productCode: `addon_${kind}_${key}`,
      detail: { orgId, kind, key, blocks, renewId, days },
      onPaid: async (transaction) => {
        await addonService.activate({ organization, kind, key, blocks, renewId, name, amount, days, transaction, by });
        await refreshOrganizations();
        reloadPurchases();
        toast(tx(`เปิดใช้ ${name} แล้ว`, "Add-on activated"), "success");
      },
    });
  };

  // ฟีเจอร์ custom: อ่านใบเสนอราคาล่าสุดจากชีตก่อนเปิด QR ทุกครั้ง แล้วใช้ยอดจากใบเสนอราคานั้นเท่านั้น
  const payCustom = async (request) => {
    let fresh;
    try {
      fresh = await customRequestService.get(orgId, request.id);
    } catch (err) {
      toast(err.message, "error");
      return;
    }
    const check = quotePayable(fresh);
    if (!check.ok) {
      reloadRequests();
      toast(fresh?.status === "quoted" ? tx(INVALID_QUOTE_MESSAGE, INVALID_QUOTE_MESSAGE_EN) : tx("คำขอนี้ไม่อยู่ในสถานะรอชำระเงิน", "This request is not awaiting payment"), "error");
      return;
    }
    const amount = check.amount;
    setCheckout({
      title: fresh.serviceKey ? fresh.title : tx(`ฟีเจอร์ custom: ${fresh.title}`, `Custom feature: ${fresh.title}`),
      amount,
      orderType: ORDER_TYPE.addon,
      reference: `custom:${fresh.id}`,
      productCode: `custom_${fresh.id}`,
      detail: { orgId, kind: "custom", requestId: fresh.id, mandays: check.mandays, ratePerManday: check.rate },
      onPaid: async (transaction) => {
        await customRequestService.markPaid({
          orgId, id: fresh.id, amount, transaction, by, idempotencyKey: `${transaction?.transactionId || ""}:custom:${fresh.id}`,
        });
        reloadRequests();
        toast(tx("ชำระเงินเรียบร้อย ทีมงานจะเริ่มดำเนินการ", "Payment complete — our team will start working"), "success");
      },
    });
  };

  const tabs = [
    { key: "like", label: tx("ปั้มไลค์", "Boost likes") },
    { key: "follow", label: tx("ปั้มฟอลโลว์", "Boost followers") },
    { key: "topup", label: tx("เติม Point", "Top up points") },
    { key: "ai", label: tx("เครดิต AI (ยังไม่เปิด)", "AI credits (not available)") },
    { key: "features", label: tx("ฟีเจอร์เสริม", "Extra features") },
    { key: "quota", label: tx("เพิ่มโควตา", "Extra quota") },
    { key: "services", label: tx("บริการติดตั้ง & อบรม", "Setup & training") },
    { key: "custom", label: tx("ฟีเจอร์ custom", "Custom features") },
  ];

  return (
    <>
      <PageHead
        title={tx("ฟีเจอร์เสริม & Point", "Add-ons & points")}
        subtitle={tx(
          "เติม point ไว้ปั้มไลค์/ฟอลโลว์ และซื้อฟีเจอร์หรือโควตาเพิ่มจากแพ็กเกจหลัก",
          "Top up points for social boosts, and buy extra features or quota on top of your plan"
        )}
      />

      <div className="grid-3 u-mb-16">
        <StatCard label={tx("Point คงเหลือ", "Point balance")} value={pts(balance || 0)} icon={<Icon name="star" />} hint={tx("ใช้ปั้มไลค์/ฟอลโลว์ ไม่มีวันหมดอายุ", "For boosts · never expires")} />
        <StatCard label={tx("ออเดอร์ปั้มที่กำลังทำ", "Boosts in progress")} value={activeOrders} icon={<Icon name="clock" />} />
        <StatCard
          label={tx("ฟีเจอร์เสริมที่ใช้อยู่", "Active add-ons")}
          value={activeAddonCount}
          icon={<Icon name="gift" />}
          hint={subscription.isActive ? tx(`หมดพร้อมแพ็กเกจ ${formatDate(subscription.expireAt)}`, `Ends with plan ${formatDate(subscription.expireAt)}`) : ""}
        />
      </div>

      <Tabs tabs={tabs} value={tab} onChange={(key) => setParams({ tab: key }, { replace: true })} />

      <div className="u-mt-16">
        {tab === "like" || tab === "follow" ? (
          <BoostTab
            type={tab}
            balance={balance || 0}
            orders={(orders || []).filter((row) => row.type === tab)}
            orgId={orgId}
            by={by}
            onBuy={buyPoints}
            onCart={cartPoints}
            onChanged={reloadPoints}
            goTopup={() => setParams({ tab: "topup" }, { replace: true })}
          />
        ) : null}

        {tab === "topup" ? <TopupTab ledger={ledger || []} onBuy={buyPoints} onCart={cartPoints} /> : null}

        {/* ฟีเจอร์เสริม/โควตา ผูกกับอายุแพ็กเกจหลัก — ยังไม่มีแพ็กเกจให้ไปซื้อก่อน */}
        {tab === "ai" ? <AiCreditTab balance={aiBalance || 0} ledger={aiLedger || []} /> : null}

        {(tab === "features" || tab === "quota" || tab === "services" || tab === "custom") && !subscription.isActive ? <NeedPlan /> : null}

        {tab === "services" && subscription.isActive ? (
          <ServiceTab
            orgId={orgId}
            by={by}
            onPay={payCustom}
            onCart={(request) => addToCart(customItem(request.id))}
            onChanged={reloadRequests}
          />
        ) : null}

        {tab === "custom" && subscription.isActive ? (
          <CustomTab
            orgId={orgId}
            by={by}
            requests={requests || []}
            onChanged={reloadRequests}
            onPay={payCustom}
            onCart={(request) => addToCart(customItem(request.id))}
            checkStorage={checkStorage}
          />
        ) : null}

        {tab === "features" && subscription.isActive ? (
          <>
            <FeatureTab subscription={subscription} addons={myAddons} onBuy={buyAddon} onCart={cartAddon} />
            <QuoteFeatureList
              orgId={orgId}
              by={by}
              requests={requests || []}
              onRequested={reloadRequests}
              goRequests={() => setParams({ tab: "custom" }, { replace: true })}
            />
          </>
        ) : null}

        {tab === "quota" && subscription.isActive ? <QuotaTab subscription={subscription} addons={myAddons} onBuy={buyAddon} onCart={cartAddon} /> : null}

        {(tab === "features" || tab === "quota") && subscription.isActive ? <PurchaseHistory rows={purchases || []} /> : null}
      </div>

      <PaymentQrModal
        open={!!checkout}
        title={checkout?.title || ""}
        subtitle={organization?.name}
        amount={checkout?.amount || 0}
        orderType={checkout?.orderType}
        reference={checkout?.reference}
        items={
          checkout
            ? [{ productName: checkout.title, productCode: checkout.productCode, quantity: 1, price: checkout.amount, detail: checkout.detail }]
            : []
        }
        onPaid={(transaction) => checkout.onPaid(transaction)}
        onClose={() => setCheckout(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// ปั้มไลค์ / ปั้มฟอลโลว์
// ---------------------------------------------------------------------------
function BoostTab({ type, balance, orders, orgId, by, onBuy, onCart, onChanged, goTopup }) {
  const { toast } = useApp();
  const { tx, pick } = useI18n();
  const boostType = BOOST_TYPES.find((row) => row.key === type);
  const [platform, setPlatform] = useState("facebook");
  const [targetUrl, setTargetUrl] = useState("");
  const [quantity, setQuantity] = useState(1000);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");

  const service = boostServiceOf(platform, type);
  const qty = Number(quantity) || 0;
  const cost = service ? boostCost(service.key, qty) : 0;
  const qtyValid = qty >= BOOST_QTY.min && qty <= BOOST_QTY.max && qty % BOOST_QTY.step === 0;
  const urlValid = validBoostUrl(platform, targetUrl);
  const enough = balance >= cost;
  const packs = POINT_PACKS.filter((row) => row.group === type);
  const unit = tx(boostType.unit, boostType.unitEn);

  const submit = async () => {
    if (!urlValid) return toast(tx(`ใส่ลิงก์ ${BOOST_PLATFORM_MAP[platform].name} ให้ถูกต้อง`, `Enter a valid ${BOOST_PLATFORM_MAP[platform].name} link`), "error");
    if (!qtyValid) return toast(tx(`จำนวนต้อง ${money(BOOST_QTY.min, 0)} - ${money(BOOST_QTY.max, 0)} และเป็นหลักร้อย`, "Quantity must be in hundreds"), "error");
    if (!enough) return toast(tx("point ไม่พอ กรุณาเติม point ก่อน", "Not enough points — please top up"), "error");

    setSaving(true);
    try {
      const order = await boostService.order({ orgId, serviceKey: service.key, targetUrl, quantity: qty, note, by });
      toast(tx(`ส่งออเดอร์ ${order.orderNo} แล้ว ทีมงานดำเนินการให้`, `Order ${order.orderNo} placed — our team will handle it`), "success");
      setTargetUrl("");
      setNote("");
      onChanged();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (row) => {
    setBusyId(row.id);
    try {
      await boostService.cancel(orgId, row.id);
      toast(tx(`คืน ${money(row.points, 0)} point แล้ว`, `${money(row.points, 0)} points refunded`), "success");
      onChanged();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <div className="grid-2" style={{ alignItems: "start" }}>
        {/* ---------- ฟอร์มสั่งปั้ม ---------- */}
        <Card
          title={tx(`สั่ง${boostType.name}ด้วย point`, `Order ${boostType.nameEn.toLowerCase()} with points`)}
          subtitle={tx("ทีมงานดำเนินการให้ด้วยมือ (ไม่ใช่ระบบอัตโนมัติ) ยกเลิกได้จนกว่าทีมงานจะเริ่ม (คืน point เต็มจำนวน)", "Handled manually by our team (not automated) · cancel for a full refund until work starts")}
        >
          <div className="field-crm">
            <label>{tx("แพลตฟอร์ม", "Platform")}</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {BOOST_PLATFORMS.map((item) => {
                const row = boostServiceOf(item.key, type);
                return (
                  <Button key={item.key} size="sm" variant={platform === item.key ? "primary" : "ghost"} onClick={() => setPlatform(item.key)}>
                    {item.name} · {row.rate} pt
                  </Button>
                );
              })}
            </div>
          </div>

          <Input
            label={type === "like" ? tx("ลิงก์โพสต์ / คลิป", "Post / video link") : tx("ลิงก์เพจ / โปรไฟล์ / ช่อง", "Page / profile / channel link")}
            placeholder={`https://www.${BOOST_PLATFORM_MAP[platform].hosts[0]}/...`}
            value={targetUrl}
            onChange={(event) => setTargetUrl(event.target.value)}
            error={targetUrl && !urlValid ? tx(`ต้องเป็นลิงก์ ${BOOST_PLATFORM_MAP[platform].name}`, `Must be a ${BOOST_PLATFORM_MAP[platform].name} link`) : ""}
            hint={tx("บัญชี/โพสต์ต้องตั้งเป็นสาธารณะ ไม่งั้นทีมงานปั้มไม่ได้", "Account/post must be public")}
          />

          <Input
            label={tx(`จำนวน (${unit})`, `Quantity (${unit})`)}
            type="number"
            min={BOOST_QTY.min}
            max={BOOST_QTY.max}
            step={BOOST_QTY.step}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            error={quantity !== "" && !qtyValid ? tx(`${money(BOOST_QTY.min, 0)} - ${money(BOOST_QTY.max, 0)} และเป็นหลักร้อย`, "In hundreds, within range") : ""}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: -6, marginBottom: 12 }}>
            {[500, 1000, 5000, 10000].map((value) => (
              <Button key={value} size="sm" variant={qty === value ? "primary" : "ghost"} onClick={() => setQuantity(value)}>
                {money(value, 0)}
              </Button>
            ))}
          </div>

          <Textarea label={tx("หมายเหตุถึงทีมงาน (ถ้ามี)", "Note to our team (optional)")} rows={2} value={note} onChange={(event) => setNote(event.target.value)} />

          <div className="card-crm" style={{ marginBottom: 12, background: "var(--c-bg-soft, #f8fafc)" }}>
            <div className="u-between u-fs-13">
              <span className="u-muted">{tx("ใช้ point", "Points needed")}</span>
              <span className="u-strong" style={{ fontSize: 18 }}>{pts(qtyValid ? cost : 0)}</span>
            </div>
            <div className="u-between u-fs-13">
              <span className="u-muted">{tx("คงเหลือหลังสั่ง", "Balance after")}</span>
              <span className={enough ? "" : "u-danger u-strong"}>{pts(balance - (qtyValid ? cost : 0))}</span>
            </div>
            {service ? (
              <div className="u-fs-12 u-muted u-mt-8">
                {tx("ทีมงานดำเนินการให้ด้วยมือ ไม่ระบุเวลาที่แน่นอน", "Handled manually by our team — no fixed timeframe")}
              </div>
            ) : null}
          </div>

          {enough || !qtyValid ? (
            <Button variant="primary" block disabled={saving || !qtyValid || !urlValid} onClick={submit}>
              {saving ? tx("กำลังส่ง...", "Sending...") : tx(`ยืนยันสั่ง${boostType.name}`, "Place order")}
            </Button>
          ) : (
            <Button variant="primary" block onClick={goTopup}>
              {tx(`point ไม่พอ (ขาด ${money(cost - balance, 0)}) · เติม point`, "Not enough points · top up")}
            </Button>
          )}
        </Card>

        {/* ---------- แพ็ค point ของประเภทนี้ ---------- */}
        <Card
          title={tx(`แพ็ค${boostType.name}`, `${boostType.nameEn} packs`)}
          subtitle={tx("ซื้อแล้วได้เป็น point ใช้ปั้มได้ทุกแพลตฟอร์ม · คุ้มกว่าเติมทั่วไป", "Paid as points usable on any platform · better value than plain top-up")}
        >
          <div style={{ display: "grid", gap: 10 }}>
            {packs.map((pack) => (
              <PackRow
                key={pack.key}
                pack={pack}
                hint={tx(
                  `≈ ${boostType.name} Facebook ${money(approxUnits(pack.points, `facebook_${type}`), 0)} ${boostType.unit}`,
                  `≈ ${money(approxUnits(pack.points, `facebook_${type}`), 0)} Facebook ${boostType.unitEn}`
                )}
                onBuy={() => onBuy({ pack })}
                onCart={() => onCart({ pack })}
                label={pick(pack)}
              />
            ))}
          </div>
        </Card>
      </div>

      {/* ---------- ออเดอร์ ---------- */}
      <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>
        {tx(`ออเดอร์${boostType.name}`, `${boostType.nameEn} orders`)}
      </div>
      <DataTable
        rows={orders}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "orderNo", label: tx("เลขออเดอร์", "Order no.") },
          { key: "platform", label: tx("แพลตฟอร์ม", "Platform"), render: (row) => BOOST_PLATFORM_MAP[row.platform]?.name || row.platform },
          {
            key: "targetUrl",
            label: tx("ลิงก์", "Link"),
            render: (row) => (
              <a href={row.targetUrl} target="_blank" rel="noopener noreferrer" style={{ maxWidth: 220, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
                {row.targetUrl}
              </a>
            ),
          },
          { key: "quantity", label: tx("จำนวน", "Qty"), align: "right", render: (row) => money(row.quantity, 0) },
          { key: "points", label: "Point", align: "right", render: (row) => money(row.points, 0) },
          {
            key: "status",
            label: tx("สถานะ", "Status"),
            render: (row) => {
              const status = BOOST_STATUS[row.status] || { th: row.status, en: row.status, tone: "gray" };
              return <Badge tone={status.tone}>{tx(status.th, status.en)}</Badge>;
            },
          },
          {
            key: "action",
            label: "",
            align: "right",
            render: (row) => {
              if (row.status === "pending") {
                return (
                  <Button size="sm" disabled={busyId === row.id} onClick={() => cancel(row)}>
                    {tx("ยกเลิก", "Cancel")}
                  </Button>
                );
              }
              // ทีมงานยกเลิกในชีต (ปั้มไม่ได้) แต่ยังไม่ได้คืน point
              if (row.status === "cancelled" && !row.refundedAt) {
                return (
                  <Button size="sm" variant="primary" disabled={busyId === row.id} onClick={() => cancel(row)}>
                    {tx("รับ point คืน", "Claim refund")}
                  </Button>
                );
              }
              return null;
            },
          },
        ]}
        emptyText={tx("ยังไม่มีออเดอร์", "No orders yet")}
      />
    </>
  );
}

function PackRow({ pack, label, hint, onBuy, onCart }) {
  const { tx } = useI18n();
  const bonus = pack.points - pack.price;
  return (
    <div className="card-crm u-between" style={{ marginBottom: 0, borderWidth: pack.featured ? 2 : 1, borderColor: pack.featured ? "var(--c-indigo-600)" : undefined }}>
      <div>
        <div className="u-strong">
          {label} {pack.featured ? <Badge tone="indigo">{tx("คุ้มสุด", "Best value")}</Badge> : null}
        </div>
        <div className="u-fs-13">
          {pts(pack.points)}
          {bonus > 0 ? <span style={{ color: "var(--c-emerald, #10b981)" }}> · {tx(`โบนัส +${money(bonus, 0)}`, `+${money(bonus, 0)} bonus`)}</span> : null}
        </div>
        {hint ? <div className="u-fs-12 u-muted">{hint}</div> : null}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <Button variant="primary" onClick={onBuy}>{baht(pack.price, 0)}</Button>
        <Button onClick={onCart} title={tx("ใส่ตะกร้า", "Add to cart")} aria-label={tx("ใส่ตะกร้า", "Add to cart")}>
          <Icon name="cartPlus" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// เติม point
// ---------------------------------------------------------------------------
function TopupTab({ ledger, onBuy, onCart }) {
  const { tx, pick } = useI18n();
  const [amount, setAmount] = useState("");
  const value = Math.floor(Number(amount) || 0);
  const points = pointsForAmount(value);

  return (
    <>
      <div className="grid-2" style={{ alignItems: "start" }}>
        <Card title={tx("แพ็คเติม point", "Point packs")} subtitle={tx("1 บาท = 1 point · เติมเยอะได้โบนัส", "1 THB = 1 point · bonus on larger top-ups")}>
          <div style={{ display: "grid", gap: 10 }}>
            {POINT_PACKS.filter((row) => row.group === "topup").map((pack) => (
              <PackRow key={pack.key} pack={pack} label={pick(pack)} onBuy={() => onBuy({ pack })} onCart={() => onCart({ pack })} />
            ))}
          </div>
        </Card>

        <Card title={tx("เติมจำนวนเอง", "Custom amount")} subtitle={tx(`ขั้นต่ำ ${money(CUSTOM_TOPUP.min, 0)} บาท สูงสุด ${money(CUSTOM_TOPUP.max, 0)} บาท`, `Min ${money(CUSTOM_TOPUP.min, 0)} · max ${money(CUSTOM_TOPUP.max, 0)} THB`)}>
          <Input
            label={tx("จำนวนเงิน (บาท)", "Amount (THB)")}
            type="number"
            min={CUSTOM_TOPUP.min}
            max={CUSTOM_TOPUP.max}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <div className="u-fs-12 u-muted u-mb-8">
            {TOPUP_BONUS.slice()
              .reverse()
              .map((row) => tx(`ตั้งแต่ ${money(row.from, 0)} บาท +${row.percent}%`, `From ${money(row.from, 0)} THB +${row.percent}%`))
              .join(" · ")}
          </div>
          <div className="u-between u-mb-16">
            <span className="u-muted">{tx("จะได้รับ", "You get")}</span>
            <span className="u-strong" style={{ fontSize: 20 }}>{pts(points)}</span>
          </div>
          <Button variant="primary" block disabled={!points} onClick={() => onBuy({ amount: value })}>
            {points ? tx(`ชำระ ${baht(value, 0)}`, `Pay ${baht(value, 0)}`) : tx("กรอกจำนวนเงิน", "Enter amount")}
          </Button>
          <Button className="u-mt-8" block disabled={!points} onClick={() => onCart({ amount: value })}>
            <Icon name="cartPlus" /> {tx("ใส่ตะกร้า", "Add to cart")}
          </Button>
          <div className="u-fs-12 u-muted u-mt-8">
            {tx("point ใช้สั่งปั้มไลค์/ฟอลโลว์เท่านั้น ไม่มีวันหมดอายุ และไม่สามารถแลกคืนเป็นเงินได้", "Points are for boosts only, never expire and are non-refundable as cash")}
          </div>
        </Card>
      </div>

      <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>
        {tx("ประวัติ point", "Point history")}
      </div>
      <DataTable
        rows={ledger}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          {
            key: "type",
            label: tx("รายการ", "Type"),
            render: (row) =>
              row.type === "topup" ? (
                <Badge tone="green">{tx("เติม", "Top-up")}</Badge>
              ) : row.type === "refund" ? (
                <Badge tone="sky">{tx("คืน", "Refund")}</Badge>
              ) : (
                <Badge tone="amber">{tx("ใช้ปั้ม", "Boost")}</Badge>
              ),
          },
          { key: "detail", label: tx("รายละเอียด", "Detail") },
          { key: "refNo", label: "Ref", render: (row) => row.refNo || row.orderNo || "-" },
          { key: "amount", label: tx("ยอดชำระ", "Paid"), align: "right", render: (row) => (row.amount ? baht(row.amount, 0) : "-") },
          {
            key: "points",
            label: "Point",
            align: "right",
            render: (row) => (
              <span className="u-strong" style={{ color: row.points > 0 ? "var(--c-emerald, #10b981)" : "var(--c-rose)" }}>
                {row.points > 0 ? "+" : ""}
                {money(row.points, 0)}
              </span>
            ),
          },
        ]}
        emptyText={tx("ยังไม่มีประวัติ point", "No point history yet")}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// ฟีเจอร์เสริม (ซื้อโมดูลแยก)
// ---------------------------------------------------------------------------
function FeatureTab({ subscription, addons, onBuy, onCart }) {
  const { tx, pick } = useI18n();
  const planModules = subscription.plan?.modules || [];
  const candidates = FEATURE_MODULES.filter((item) => MODULE_ADDON_PRICES[item.key] && !planModules.includes(item.key));

  if (!candidates.length) {
    return (
      <Card className="u-center" style={{ padding: 28 }}>
        <div style={{ fontSize: 36, color: "var(--c-indigo-600)" }}><Icon name="success" /></div>
        <div className="u-strong u-mt-8">{tx("แพ็กเกจของคุณมีครบทุกฟีเจอร์แล้ว", "Your plan already includes every feature")}</div>
      </Card>
    );
  }

  return (
    <Card
      title={tx("ซื้อฟีเจอร์เสริม", "Buy extra features")}
      subtitle={tx(
        `เปิดใช้โมดูลที่แพ็กเกจ ${subscription.plan ? subscription.plan.name : ""} ไม่มี · คิดเงินตามวันที่เหลือ และหมดอายุพร้อมแพ็กเกจหลัก (${formatDate(subscription.expireAt)})`,
        `Unlock modules outside your plan · prorated to your plan's remaining days, ends ${formatDate(subscription.expireAt)}`
      )}
    >
      <div className="grid-3" style={{ gap: 12 }}>
        {candidates.map((item) => {
          const owned = addons.filter((row) => row.kind === "module" && row.key === item.key).sort((a, b) => new Date(b.expireAt) - new Date(a.expireAt))[0];
          const active = owned && new Date(owned.expireAt) > new Date();
          const needsRenew = owned && new Date(owned.expireAt) < new Date(subscription.expireAt);
          const start = active ? owned.expireAt : undefined;
          const { days, amount } = proratePrice(MODULE_ADDON_PRICES[item.key], subscription.expireAt, start);
          const upgrade = lowestPlanWith(item.key);

          return (
            <div key={item.key} className="card-crm" style={{ marginBottom: 0, display: "flex", flexDirection: "column" }}>
              <div className="u-between">
                <div className="u-strong">
                  <Icon name={item.icon} /> {pick(item)}
                </div>
                {active ? <Badge tone="green">{tx("ใช้งานอยู่", "Active")}</Badge> : owned ? <Badge tone="gray">{tx("หมดอายุ", "Expired")}</Badge> : null}
              </div>
              <div className="u-fs-12 u-muted" style={{ flex: "1 1 auto" }}>{String(item.detail || "").replace(/\s*\(กำลังเปิดให้ใช้งาน\)/, "")}</div>
              <div className="u-mt-8">
                <span className="u-strong" style={{ fontSize: 20 }}>{baht(MODULE_ADDON_PRICES[item.key], 0)}</span>
                <span className="u-fs-12 u-muted"> / {tx("เดือน", "month")}</span>
              </div>
              {active ? (
                <div className="u-fs-12 u-muted">{tx(`ใช้ได้ถึง ${formatDate(owned.expireAt)}`, `Until ${formatDate(owned.expireAt)}`)}</div>
              ) : null}
              {item.comingSoon ? (
                <div className="u-fs-12" style={{ color: "#c2410c" }}>
                  {tx("ยังไม่เปิดให้บริการ", "Not available yet")}
                </div>
              ) : item.key === "etax" ? (
                <div className="u-fs-12" style={{ color: "#c2410c" }}>
                  {tx("ยังไม่เชื่อมต่อผู้ให้บริการ e-Tax — ออก/พิมพ์เอกสารได้ แต่ยังไม่ส่งกรมสรรพากร", "e-Tax provider not connected — documents can be issued/printed but are not sent to the Revenue Department")}
                </div>
              ) : item.key === "online_payment" ? (
                <div className="u-fs-12" style={{ color: "#c2410c" }}>
                  {tx("หน้าจอใช้งานกำลังเปิดให้ใช้งาน ซื้อสิทธิ์ล่วงหน้าได้", "The feature screens are rolling out — you can buy access in advance")}
                </div>
              ) : null}
              {upgrade ? (
                <div className="u-fs-12 u-muted">{tx(`หรืออัปเกรดเป็น ${upgrade.name} ได้ทั้งชุด`, `Or upgrade to ${upgrade.nameEn}`)}</div>
              ) : null}

              {/* comingSoon = ยังใช้งานไม่ได้จริง จึงซ่อนปุ่มซื้อ/ใส่ตะกร้า ไม่ขายของที่ยังไม่พร้อม */}
              {(!active || needsRenew) && !item.comingSoon ? (
                <Button
                  className="u-mt-8"
                  variant="primary"
                  block
                  onClick={() => onBuy({ kind: "module", key: item.key, renewId: owned?.id || "", start })}
                >
                  {owned
                    ? tx(`ต่ออายุ ${days} วัน · ${baht(amount, 0)}`, `Renew ${days} days · ${baht(amount, 0)}`)
                    : tx(`ซื้อ ${days} วัน · ${baht(amount, 0)}`, `Buy ${days} days · ${baht(amount, 0)}`)}
                </Button>
              ) : null}
              {(!active || needsRenew) && !item.comingSoon ? (
                <Button className="u-mt-8" block onClick={() => onCart({ kind: "module", key: item.key, renewId: owned?.id || "" })}>
                  <Icon name="cartPlus" /> {tx("ใส่ตะกร้า", "Add to cart")}
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// เครดิต AI (กระเป๋าแยกจาก point ปั้ม)
// ---------------------------------------------------------------------------
function AiCreditTab({ balance, ledger }) {
  const { tx } = useI18n();
  return (
    <>
      <div className="grid-2" style={{ alignItems: "start" }}>
        {/* ซ่อนแพ็คซื้อเครดิต AI: ฟีเจอร์ AI ยังไม่เปิดให้บริการ เครดิตที่ซื้อไปจะยังใช้ไม่ได้ จึงไม่เปิดขาย */}
        <Card
          title={tx("แพ็คเครดิต AI", "AI credit packs")}
          subtitle={tx("ยังไม่เปิดให้บริการ", "Not available yet")}
        >
          <div className="u-fs-13 u-muted">
            {tx("ฟีเจอร์ AI ยังไม่เปิดให้บริการ จึงยังไม่เปิดขายเครดิต AI", "AI features are not available yet, so AI credits are not on sale")}
          </div>
        </Card>
        <Card title={tx("เครดิต AI คงเหลือ", "AI credit balance")}>
          <div className="u-strong" style={{ fontSize: 28 }}>{money(balance, 0)}</div>
          <div className="u-fs-12 u-muted u-mt-8">
            {tx(
              "ฟีเจอร์ AI ยังไม่เปิดให้บริการ เครดิตที่มีอยู่เดิมจะยังไม่ถูกใช้ · ไม่มีวันหมดอายุ และแลกคืนเป็นเงินไม่ได้ · ใช้ปั้มไลค์/ฟอลโลว์ไม่ได้",
              "AI features are not available yet — existing credits are not consumed · never expire, non-refundable, not usable for boosts"
            )}
          </div>
        </Card>
      </div>
      <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>
        {tx("ประวัติเครดิต AI", "AI credit history")}
      </div>
      <DataTable
        rows={ledger}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "detail", label: tx("รายละเอียด", "Detail") },
          { key: "refNo", label: "Ref", render: (row) => row.refNo || "-" },
          { key: "points", label: tx("เครดิต", "Credits"), align: "right", render: (row) => money(row.points, 0) },
        ]}
        emptyText={tx("ยังไม่มีประวัติเครดิต AI", "No AI credit history yet")}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// บริการติดตั้ง & อบรม (ราคาคงที่ จ่ายครั้งเดียว — ใช้ flow ใบเสนอราคาเดิม)
// ---------------------------------------------------------------------------
function ServiceTab({ orgId, by, onPay, onCart, onChanged }) {
  const { tx, pick } = useI18n();
  const { toast } = useApp();
  const [busyKey, setBusyKey] = useState("");

  const order = async (item, then) => {
    setBusyKey(item.key);
    try {
      const request = await customRequestService.orderService({ orgId, serviceKey: item.key, by });
      onChanged();
      await then(request);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <Card
      title={tx("บริการติดตั้ง & อบรม", "Setup & training services")}
      subtitle={tx("จ่ายครั้งเดียว ราคายังไม่รวม VAT 7% · หลังชำระทีมงานติดต่อนัดวันเริ่มงาน ติดตามสถานะได้ที่แท็บฟีเจอร์ custom", "One-time, excl. 7% VAT · our team schedules the work after payment; track it in the Custom features tab")}
    >
      <div className="grid-2" style={{ gap: 12 }}>
        {SERVICE_PACKAGES.map((item) => (
          <div key={item.key} className="card-crm" style={{ marginBottom: 0, display: "flex", flexDirection: "column" }}>
            <div className="u-strong">
              <Icon name={item.icon} /> {pick(item)}
            </div>
            <div className="u-fs-12 u-muted" style={{ flex: "1 1 auto" }}>{item.detail}</div>
            <div className="u-mt-8">
              <span className="u-strong" style={{ fontSize: 20 }}>{baht(item.mandays * item.rate, 0)}</span>
              <span className="u-fs-12 u-muted"> · {tx(`${item.mandays} manday`, `${item.mandays} mandays`)}</span>
            </div>
            <Button className="u-mt-8" variant="primary" block disabled={busyKey === item.key} onClick={() => order(item, onPay)}>
              {tx("ซื้อบริการนี้", "Buy this service")}
            </Button>
            <Button className="u-mt-8" block disabled={busyKey === item.key} onClick={() => order(item, onCart)}>
              <Icon name="cartPlus" /> {tx("ใส่ตะกร้า", "Add to cart")}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ฟีเจอร์เสริมแบบขอใบเสนอราคา — ทีมงานประเมินราคาให้แต่ละร้าน (ใช้ flow คำขอ custom)
// ---------------------------------------------------------------------------
function QuoteFeatureList({ orgId, by, requests, onRequested, goRequests }) {
  const { tx, pick } = useI18n();
  const { toast } = useApp();
  const [busyKey, setBusyKey] = useState("");

  // คำขอล่าสุดของแต่ละฟีเจอร์ (ไม่นับที่ยกเลิก) — requests เรียงใหม่สุดก่อนแล้ว
  const requestOf = (item) => requests.find((row) => row.title === quoteFeatureTitle(item) && row.status !== "cancelled");

  const request = async (item) => {
    setBusyKey(item.key);
    try {
      await customRequestService.create({
        orgId,
        title: quoteFeatureTitle(item),
        description: `ขอใบเสนอราคาฟีเจอร์เสริม "${item.name}" (${item.detail})`,
        by,
      });
      onRequested();
      toast(tx("ส่งคำขอแล้ว ทีมงานจะเสนอราคาให้ในแท็บฟีเจอร์ custom", "Request sent — our team will post a quotation in the Custom features tab"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <Card
      className="u-mt-16"
      title={tx("ฟีเจอร์เสริม (ราคาตามใบเสนอราคา)", "More features (quoted per shop)")}
      subtitle={tx("ทีมงานประเมินราคาให้แต่ละร้าน กดขอใบเสนอราคาแล้วติดตาม/ชำระเงินได้ที่แท็บฟีเจอร์ custom", "Our team quotes each shop — request a quote, then track and pay in the Custom features tab")}
    >
      <div className="grid-3" style={{ gap: 12 }}>
        {QUOTE_FEATURES.map((item) => {
          const row = requestOf(item);
          const status = row ? CUSTOM_STATUS[row.status] : null;
          return (
            <div key={item.key} className="card-crm" style={{ marginBottom: 0, display: "flex", flexDirection: "column" }}>
              <div className="u-between">
                <div className="u-strong">
                  <Icon name={item.icon} /> {pick(item)}
                </div>
                {status ? <Badge tone={status.tone}>{tx(status.th, status.en)}</Badge> : null}
              </div>
              <div className="u-fs-12 u-muted" style={{ flex: "1 1 auto" }}>{item.detail}</div>
              <div className="u-mt-8 u-strong">{tx("ราคาตามใบเสนอราคา", "Quoted price")}</div>
              {row ? (
                <Button className="u-mt-8" block onClick={goRequests}>
                  {tx("ดูคำขอ", "View request")}
                </Button>
              ) : (
                <Button className="u-mt-8" variant="primary" block disabled={busyKey === item.key} onClick={() => request(item)}>
                  {busyKey === item.key ? tx("กำลังส่ง...", "Sending...") : tx("ขอใบเสนอราคา", "Request a quote")}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// อัปเกรดแพ็คเสริม (เพิ่มโควตา)
// ---------------------------------------------------------------------------
function QuotaTab({ subscription, addons, onBuy, onCart }) {
  const { tx, pick } = useI18n();
  const { limitOf } = useApp();
  const [blocks, setBlocks] = useState({ branches: 1, staff: 1, customers: 1, storage: 1 });

  const quotaAddons = useMemo(
    () => addons.filter((row) => row.kind === "quota").sort((a, b) => new Date(b.expireAt) - new Date(a.expireAt)),
    [addons]
  );

  return (
    <>
      <Card
        title={tx("เพิ่มโควตา", "Add quota")}
        subtitle={tx(
          `เพิ่มสาขา / พนักงาน / ลูกค้า เกินโควตาแพ็กเกจ · คิดตามวันที่เหลือ หมดอายุพร้อมแพ็กเกจหลัก (${formatDate(subscription.expireAt)})`,
          `Extend branches / staff / customers · prorated, ends with your plan (${formatDate(subscription.expireAt)})`
        )}
      >
        <div className="grid-3" style={{ gap: 12 }}>
          {/* แพ็กเกจต่อ User: เพิ่มพนักงาน = เพิ่มจำนวน User ที่หน้าแพ็กเกจ */}
          {QUOTA_ADDONS.filter((item) => !(item.key === "staff" && subscription.plan?.perUser)).map((item) => {
            const base = planLimit(subscription.planKey, item.key);
            const unlimited = base === null;
            const count = blocks[item.key];
            const { days, amount } = proratePrice(item.price * count, subscription.expireAt);
            return (
              <div key={item.key} className="card-crm" style={{ marginBottom: 0 }}>
                <div className="u-strong">{pick(item)}</div>
                <div className="u-fs-12 u-muted">
                  {tx(`+${money(item.block, 0)} ${item.unit} · ${baht(item.price, 0)} / เดือน`, `+${money(item.block, 0)} ${item.unitEn} · ${baht(item.price, 0)} / month`)}
                </div>
                <div className="u-fs-13 u-mt-8">
                  {tx("โควตาตอนนี้", "Current quota")}:{" "}
                  <span className="u-strong">{unlimited ? tx("ไม่จำกัด", "Unlimited") : money(limitOf(item.key), 0)}</span>
                  {!unlimited && limitOf(item.key) > base ? (
                    <span className="u-fs-12 u-muted"> ({tx(`แพ็กเกจ ${money(base, 0)} + เสริม ${money(limitOf(item.key) - base, 0)}`, `plan ${money(base, 0)} + extra ${money(limitOf(item.key) - base, 0)}`)})</span>
                  ) : null}
                </div>

                {unlimited ? (
                  <div className="u-fs-12 u-muted u-mt-8">{tx("แพ็กเกจของคุณไม่จำกัดอยู่แล้ว", "Already unlimited on your plan")}</div>
                ) : (
                  <>
                    <div className="u-between u-mt-8">
                      <span className="u-fs-13">{tx("จำนวนก้อน", "Blocks")}</span>
                      <Qty value={count} min={1} max={20} onChange={(value) => setBlocks((prev) => ({ ...prev, [item.key]: value }))} />
                    </div>
                    <div className="u-fs-12 u-muted">
                      {tx(`ได้เพิ่ม ${money(item.block * count, 0)} ${item.unit}`, `+${money(item.block * count, 0)} ${item.unitEn}`)}
                    </div>
                    <Button className="u-mt-8" variant="primary" block onClick={() => onBuy({ kind: "quota", key: item.key, blocks: count })}>
                      {tx(`ซื้อ ${days} วัน · ${baht(amount, 0)}`, `Buy ${days} days · ${baht(amount, 0)}`)}
                    </Button>
                    <Button className="u-mt-8" block onClick={() => onCart({ kind: "quota", key: item.key, blocks: count })}>
                      <Icon name="cartPlus" /> {tx("ใส่ตะกร้า", "Add to cart")}
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {quotaAddons.length ? (
        <>
          <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>
            {tx("โควตาเสริมของฉัน", "My extra quota")}
          </div>
          <DataTable
            rows={quotaAddons}
            columns={[
              { key: "key", label: tx("รายการ", "Item"), render: (row) => pick(QUOTA_ADDON_MAP[row.key] || {}) },
              {
                key: "amount",
                label: tx("จำนวน", "Amount"),
                align: "right",
                render: (row) => `+${money(row.amount, 0)} ${tx(QUOTA_ADDON_MAP[row.key]?.unit || "", QUOTA_ADDON_MAP[row.key]?.unitEn || "")}`,
              },
              { key: "expireAt", label: tx("ใช้ได้ถึง", "Valid until"), render: (row) => formatDate(row.expireAt) },
              {
                key: "status",
                label: tx("สถานะ", "Status"),
                render: (row) =>
                  new Date(row.expireAt) > new Date() ? <Badge tone="green">{tx("ใช้งานอยู่", "Active")}</Badge> : <Badge tone="gray">{tx("หมดอายุ", "Expired")}</Badge>,
              },
              {
                key: "action",
                label: "",
                align: "right",
                render: (row) => {
                  if (planLimit(subscription.planKey, row.key) === null) return null;
                  if (new Date(row.expireAt) >= new Date(subscription.expireAt)) return null;
                  const item = QUOTA_ADDON_MAP[row.key];
                  const count = Math.max(1, Math.round((Number(row.amount) || 0) / (item?.block || 1)));
                  const start = new Date(row.expireAt) > new Date() ? row.expireAt : undefined;
                  const { days, amount } = proratePrice((item?.price || 0) * count, subscription.expireAt, start);
                  return (
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      <Button size="sm" variant="primary" onClick={() => onBuy({ kind: "quota", key: row.key, blocks: count, renewId: row.id, start })}>
                        {tx(`ต่ออายุ ${days} วัน · ${baht(amount, 0)}`, `Renew ${days} days · ${baht(amount, 0)}`)}
                      </Button>
                      <Button size="sm" onClick={() => onCart({ kind: "quota", key: row.key, blocks: count, renewId: row.id })} title={tx("ใส่ตะกร้า", "Add to cart")} aria-label={tx("ใส่ตะกร้า", "Add to cart")}>
                        <Icon name="cartPlus" />
                      </Button>
                    </span>
                  );
                },
              },
            ]}
          />
        </>
      ) : null}

      <div className="u-fs-12 u-muted u-mt-8">
        {tx("ต้องการโควตาเยอะ? การอัปเกรดแพ็กเกจหลักอาจคุ้มกว่า", "Need a lot more? Upgrading your plan may be better value")} ·{" "}
        <Link to="/admin/subscription">{tx("ดูแพ็กเกจ", "See plans")}</Link>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ฟีเจอร์ custom (ตามคำขอลูกค้า)
// ---------------------------------------------------------------------------
const emptyForm = { title: "", description: "", linksText: "", wantedAt: "" };

function CustomTab({ orgId, by, requests, onChanged, onPay, onCart, checkStorage }) {
  const { tx } = useI18n();
  const { toast } = useApp();
  const [form, setForm] = useState(emptyForm);
  const [attachment, setAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");

  const onFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > ATTACH_MAX_BYTES) {
      toast(tx(`ไฟล์แนบต้องไม่เกิน ${Math.round(ATTACH_MAX_BYTES / 1024)} KB (ไฟล์ใหญ่ให้แนบเป็นลิงก์)`, `Attachment must be under ${Math.round(ATTACH_MAX_BYTES / 1024)} KB — use a link for larger files`), "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAttachment({ name: file.name, sizeBytes: file.size, dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const message = validateRequestForm({ ...form, attachBytes: attachment?.sizeBytes || 0 });
    if (message) return toast(message, "error");
    setSaving(true);
    try {
      if (attachment && !checkStorage(await storageService.usedBytes(orgId), attachment.sizeBytes).ok) {
        throw new Error(tx("พื้นที่เก็บไฟล์เต็ม ไม่สามารถแนบไฟล์ได้ (ลองแนบเป็นลิงก์แทน หรือเพิ่มที่เก็บไฟล์)", "Storage is full — attach a link instead or add storage"));
      }
      await customRequestService.create({ orgId, ...form, attachment, by });
      setForm(emptyForm);
      setAttachment(null);
      onChanged();
      toast(tx("ส่งคำขอแล้ว ทีมงานจะเสนอราคาให้ในหน้านี้", "Request sent — our team will post a quotation here"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (row) => {
    setBusyId(row.id);
    try {
      await customRequestService.cancel(orgId, row.id);
      toast(tx("ยกเลิกคำขอแล้ว", "Request cancelled"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusyId("");
      onChanged();
    }
  };

  return (
    <>
      <div className="grid-2" style={{ alignItems: "start" }}>
        <Card
          title={tx("ขอฟีเจอร์ custom", "Request a custom feature")}
          subtitle={tx("บอกสิ่งที่ต้องการ ทีมงานประเมินแล้วเสนอราคาเป็น manday ให้ในหน้านี้", "Tell us what you need — our team replies with a quotation in mandays")}
        >
          <div className="card-crm u-mb-16" style={{ marginBottom: 12, background: "#eef2ff", borderColor: "#c7d2fe" }}>
            <div className="u-strong u-fs-13">
              {tx(`ราคาขั้นต่ำ ${money(CUSTOM_MIN_RATE, 0)} บาท / manday`, `Minimum ${money(CUSTOM_MIN_RATE, 0)} THB per manday`)}
            </div>
            <div className="u-fs-12 u-muted">
              {tx("ยอดสุทธิ = จำนวน manday × อัตราต่อ manday (ยังไม่รวม VAT 7%) · ชำระได้หลังยอมรับใบเสนอราคา", "Total = mandays × rate per manday (excl. 7% VAT) · pay after you accept the quotation")}
            </div>
          </div>

          <Input label={tx("หัวข้อ", "Title")} maxLength={120} value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          <Textarea
            label={tx("รายละเอียดที่ต้องการ", "Details")}
            rows={4}
            maxLength={2000}
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
          <Textarea
            label={tx("ลิงก์อ้างอิง (บรรทัดละ 1 ลิงก์ ไม่เกิน 5)", "Reference links (one per line, max 5)")}
            rows={2}
            placeholder="https://..."
            value={form.linksText}
            onChange={(event) => setForm({ ...form, linksText: event.target.value })}
          />
          <div className="field-crm">
            <label>{tx(`ไฟล์แนบเล็ก (ไม่เกิน ${Math.round(ATTACH_MAX_BYTES / 1024)} KB · ไฟล์ใหญ่ให้แนบเป็นลิงก์)`, `Small attachment (max ${Math.round(ATTACH_MAX_BYTES / 1024)} KB — use a link for larger files)`)}</label>
            <input type="file" onChange={onFile} />
            {attachment ? <div className="u-fs-12 u-muted">{attachment.name} · {Math.max(1, Math.round(attachment.sizeBytes / 1024))} KB</div> : null}
          </div>
          <Input
            label={tx("วันที่อยากได้", "Wanted by")}
            type="date"
            value={form.wantedAt}
            onChange={(event) => setForm({ ...form, wantedAt: event.target.value })}
          />
          <Button variant="primary" block disabled={saving} onClick={submit}>
            {saving ? tx("กำลังส่ง...", "Sending...") : tx("ส่งคำขอ", "Send request")}
          </Button>
        </Card>

        <Card title={tx("ขั้นตอน", "How it works")}>
          <ol className="u-fs-13" style={{ paddingLeft: 18, margin: 0, display: "grid", gap: 6 }}>
            <li>{tx("ส่งคำขอ — สถานะ “รอเสนอราคา”", "Send a request — status “Awaiting quote”")}</li>
            <li>{tx("ทีมงานประเมินและเสนอราคา (manday × อัตราต่อ manday)", "Our team posts a quotation (mandays × rate)")}</li>
            <li>{tx("ตรวจใบเสนอราคาแล้วกด “ยอมรับและชำระ” หรือ “ใส่ตะกร้า”", "Review it, then “Accept & pay” or add to cart")}</li>
            <li>{tx("ชำระแล้วทีมงานเริ่มงาน (กำลังดำเนินการ → เสร็จสิ้น)", "After payment our team starts (In progress → Done)")}</li>
          </ol>
          <div className="u-fs-12 u-muted u-mt-8">
            {tx("ยกเลิกคำขอได้ก่อนชำระเงินเท่านั้น", "You can cancel only before paying")}
          </div>
        </Card>
      </div>

      <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>
        {tx("คำขอของฉัน", "My requests")}
      </div>
      <DataTable
        rows={requests}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "requestNo", label: tx("เลขที่", "No.") },
          { key: "title", label: tx("หัวข้อ", "Title") },
          { key: "wantedAt", label: tx("อยากได้ภายใน", "Wanted by"), render: (row) => (row.wantedAt ? formatDate(row.wantedAt) : "-") },
          {
            key: "quote",
            label: tx("ใบเสนอราคา", "Quotation"),
            render: (row) => {
              if (row.status === "pending_quote") return <span className="u-muted">{tx("รอทีมงานเสนอราคา", "Waiting for quotation")}</span>;
              if (row.status === "cancelled" && !row.amount) return "-";
              const check = quotePayable({ ...row, status: "quoted" });
              if (!check.ok) return <span className="u-danger u-fs-13">{tx(INVALID_QUOTE_MESSAGE, INVALID_QUOTE_MESSAGE_EN)}</span>;
              return (
                <div className="u-fs-13">
                  <div className="u-strong">{baht(check.amount, 0)}</div>
                  <div className="u-fs-12 u-muted">{money(check.mandays, 2)} manday × {baht(check.rate, 0)}</div>
                  {row.quoteNote ? <div className="u-fs-12 u-muted">{row.quoteNote}</div> : null}
                </div>
              );
            },
          },
          {
            key: "status",
            label: tx("สถานะ", "Status"),
            render: (row) => {
              const status = CUSTOM_STATUS[row.status] || { th: row.status, en: row.status, tone: "gray" };
              return <Badge tone={status.tone}>{tx(status.th, status.en)}</Badge>;
            },
          },
          {
            key: "action",
            label: "",
            align: "right",
            render: (row) => (
              <span style={{ display: "inline-flex", gap: 6 }}>
                {quotePayable(row).ok ? (
                  <>
                    <Button size="sm" variant="primary" onClick={() => onPay(row)}>{tx("ยอมรับและชำระ", "Accept & pay")}</Button>
                    <Button size="sm" onClick={() => onCart(row)} title={tx("ใส่ตะกร้า", "Add to cart")} aria-label={tx("ใส่ตะกร้า", "Add to cart")}>
                      <Icon name="cartPlus" />
                    </Button>
                  </>
                ) : null}
                {canCancelRequest(row) ? (
                  <Button size="sm" disabled={busyId === row.id} onClick={() => cancel(row)}>{tx("ยกเลิก", "Cancel")}</Button>
                ) : null}
              </span>
            ),
          },
        ]}
        emptyText={tx("ยังไม่มีคำขอ", "No requests yet")}
      />
    </>
  );
}

function NeedPlan() {
  const { tx } = useI18n();
  return (
    <Card className="u-center" style={{ padding: 28 }}>
      <div style={{ fontSize: 36, color: "var(--c-indigo-600)" }}><Icon name="lock" /></div>
      <div className="u-strong u-mt-8">{tx("ฟีเจอร์เสริมและโควตาเพิ่ม ต้องมีแพ็กเกจหลักก่อน", "Extra features and quota need an active plan")}</div>
      <div className="u-fs-13 u-muted">
        {tx("ฟีเจอร์เสริมจะหมดอายุพร้อมแพ็กเกจหลัก · ส่วนปั้มไลค์/ฟอลโลว์และเติม point ใช้ได้เลยไม่ต้องมีแพ็กเกจ", "Add-ons end with your plan · boosts and points work without a plan")}
      </div>
      <Link to="/admin/subscription" className="u-mt-16" style={{ display: "inline-block" }}>
        <Button variant="primary">{tx("เลือกแพ็กเกจ", "Choose a plan")}</Button>
      </Link>
    </Card>
  );
}

function PurchaseHistory({ rows }) {
  const { tx } = useI18n();
  return (
    <>
      <div className="u-strong u-mt-16 u-mb-8" style={{ fontSize: 15 }}>
        {tx("ประวัติการซื้อฟีเจอร์เสริม", "Add-on purchase history")}
      </div>
      <DataTable
        rows={rows}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "name", label: tx("รายการ", "Item") },
          { key: "action", label: "", render: (row) => (row.action === "renew" ? <Badge tone="sky">{tx("ต่ออายุ", "Renew")}</Badge> : <Badge tone="indigo">{tx("ซื้อใหม่", "New")}</Badge>) },
          { key: "days", label: tx("จำนวนวัน", "Days"), align: "right" },
          { key: "expireAt", label: tx("ใช้ได้ถึง", "Valid until"), render: (row) => formatDate(row.expireAt) },
          { key: "refNo", label: "Ref" },
          { key: "amount", label: tx("ยอดชำระ", "Amount"), align: "right", render: (row) => baht(row.amount) },
        ]}
        emptyText={tx("ยังไม่เคยซื้อฟีเจอร์เสริม", "No add-on purchases yet")}
      />
    </>
  );
}
